import { useAui } from '@assistant-ui/react';
import { IconDatabaseShare } from '@tabler/icons-react';
import { use } from 'react';
import { z } from 'zod';

import { type AstNode, NO_POS } from '#core/lib/expr/ast.ts';
import { parse } from '#core/lib/expr/parser.ts';
import { KNOWLEDGE_GRAPH_GROUP } from '#core/lib/expr/providers/groups.ts';
import { describeColumnDocType, type ViewDescription } from '#core/lib/expr/schema/types.ts';
import { type EvalDeps, useEvalDeps } from '#core/lib/expr/session/EvalDepsContext.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import { expectDataset } from '#core/lib/expr/value.ts';
import { displaySymbolForType } from '#core/lib/units/registry.ts';

import { listDescribableViewNames } from '#plugin-chat/components/tools/describe-views/describable-views.ts';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { Expr, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { DatasetPreview } from '#plugin-chat/lib/tool/DatasetPreview.tsx';
import { expressionSchema, outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetSummary } from '#plugin-chat/lib/tool/dataset-summary.ts';
import { ViewsNotDescribedError } from '#plugin-chat/lib/tool/errors/index.ts';
import { ToolErrorMessage } from '#plugin-chat/lib/tool/message-components.tsx';
import { toolCallPartsOf } from '#plugin-chat/lib/tool/output-types.ts';
import { materializeAndSummarize, sourceLabelOf } from '#plugin-chat/lib/tool/recovery.ts';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';
import { expressionArgsIdentity } from '#plugin-chat/lib/tool/tool-identity.ts';
import type { ToolResult } from '#plugin-chat/lib/transport/types.ts';
import { useThreadScopedEval } from '#plugin-chat/session/useThreadScopedEval.ts';

/**
 * LLM-facing input for {@link QueryKnowledgeGraphTool}. The LLM writes a
 * single s-expression (typically wrapping
 * `(query "View" (filter #(…)) (columns [:a :b]) (limit N))`)
 * that evaluates to a `Dataset`, plus an `outputDataset` key. The query is
 * executed and the materialised result is staged under `outputDataset`;
 * downstream tools reference it by that name (e.g.
 * `(-> (col <outputDataset>) …)`) — they do not re-pass the s-expression.
 */
const QueryKnowledgeGraphInputSchema = z.object({
  expression: expressionSchema.describe(
    'An s-expression that evaluates to a Dataset. Typically built around ' +
      '`(query "View" (filter #(predicate-body)) (columns [:a :b]) (limit N))`. ' +
      "The `(filter …)` form's lambda mirrors `(filter ds <fn>)`'s 2nd arg (row columns via `(:col %)`); " +
      "the `(columns …)` form mirrors `(columns ds [:a :b])`'s 2nd arg (CDF returns only the listed properties). " +
      'For ordering, chain `(sort …)` after the query — CDF cannot guarantee multi-instance sort.',
  ),
  outputDataset: outputDatasetSchema,
});

type QueryKnowledgeGraphInput = z.infer<typeof QueryKnowledgeGraphInputSchema>;

/**
 * Result envelope returned from the tool's `execute` to the LLM. The
 * expression is evaluated **and materialised** (CDF round-trip) at
 * execute time, so the LLM receives a real column profile + row sample
 * — not just a schema — and any CDF failure surfaces through the
 * structured error envelope instead of only in the UI. The materialised
 * result is cached on `session.fetch`, so the later `render`
 * (`useMaterializedExpr`) and any chained tool reuse it without a second
 * round-trip.
 */
type QueryKnowledgeGraphOutput = Readonly<{
  /** Success discriminator — every tool success envelope carries this
   * (matches `ToolOutputSuccessSchema`); failures carry `success: false`
   * via the structured error envelope instead. */
  success: true;
  /** `MaterializedDataset.state` tag — `'materialized'` after the eager
   * materialisation; kept as a string so a future direct-materialized
   * path round-trips verbatim. */
  state: string;
  /** Source descriptor's origin label (the CDF view externalId, or
   * `'materialized'` when the expression is already materialised). */
  source: string;
  /** Key the materialised result was staged under — downstream tools
   * reference this name instead of re-passing the expression. */
  outputDataset: string;
  /** Generic dataset summary (column profile + capped sample +
   * data-quality notes) rendered for the LLM via `<DatasetPreview>`. */
  summary: DatasetSummary;
}> &
  ToolResult;

/**
 * Render a view's schema as a compact markdown table. Used by the describe-first
 * guard to embed the schema of an un-described view directly in its error, so a
 * skipped `describeViews` still hands the agent real column names to retry
 * against instead of forcing a blind second guess.
 *
 * FIXME: Return JSX and render with reconciler
 */
const formatViewSchemaMarkdown = (externalId: string, doc: ViewDescription): string => {
  const rows = doc.columns.map((column) => {
    const type = describeColumnDocType(column);
    const unit = displaySymbolForType(column.type);
    return `| \`${column.name}\` | ${type} | ${unit} | ${column.description ?? ''} |`;
  });
  return [
    `### ${externalId}`,
    ...(doc.description ? [doc.description] : []),
    '',
    '| column | type | unit | description |',
    '| --- | --- | --- | --- |',
    ...rows,
  ].join('\n');
};

// The view name in a `(query "X" …)` form is schema-pinned to a leading string
// literal (see providers/query-op.ts), so we only need to recognise that one
// shape; every other node just recurses into its children. Dispatch by node
// kind via a per-variant lookup (each handler typed on its narrowed node) so
// adding a container shape is a single entry, never an if/else chain.
const viewNamesByKind: {
  [NodeType in AstNode as NodeType['kind']]?: (node: NodeType) => string[];
} = {
  list: (node) => {
    const head = node.args[0]?.value;
    const queried =
      node.op === 'query' && head?.kind === 'literal' && typeof head.value === 'string' ? [head.value] : [];
    return [...queried, ...node.args.flatMap((arg) => viewNamesIn(arg.value))];
  },
  vector: (node) => node.elements.flatMap(viewNamesIn),
  map: (node) => node.entries.flatMap((entry) => viewNamesIn(entry.value)),
};

// The indexed handler is keyed by `node.kind`, so it only ever receives its own
// narrowed variant; the cast restores that correlation the index access erases.
const viewNamesIn = (node: AstNode): string[] =>
  (viewNamesByKind[node.kind] as ((node: AstNode) => string[]) | undefined)?.(node) ?? [];

/**
 * Statically collect every view referenced by a `(query "X" …)` form, without
 * evaluating. Returns `[]` on a parse error so a genuine syntax error surfaces
 * through normal evaluation rather than being masked by the guard.
 */
const extractQueriedViewNames = (expression: string): string[] => {
  try {
    return [...new Set(parse(expression).flatMap(viewNamesIn))];
  } catch {
    return [];
  }
};

type TranscriptMessage = { readonly content?: readonly unknown[] };

/**
 * Hard-deny a knowledge-graph query that references a describable view not yet
 * described on this branch. Runs on the static s-expression before any CDF
 * round trip; throws {@link ViewsNotDescribedError} with the missing view
 * schema embedded so the agent can correct column names and retry in one step.
 * Views whose provider is not describable (e.g. raw `cdf-view` facades) are
 * exempt — requiring a describe the tool won't offer would be an unsatisfiable
 * loop.
 */
const assertViewsDescribed = (input: {
  expression: string;
  messages: readonly TranscriptMessage[];
  deps: Pick<EvalDeps, 'viewNames' | 'getProvider'>;
  describeViewsToolName: string;
}): void => {
  const queried = extractQueriedViewNames(input.expression);
  if (queried.length === 0) {
    return;
  }

  const describable = new Set(listDescribableViewNames(input.deps));
  const described = new Set(
    toolCallPartsOf(input.messages)
      .filter((part) => part.toolName === input.describeViewsToolName)
      .flatMap((part) => {
        const views = part.args.views;
        return Array.isArray(views) ? views.filter((view): view is string => typeof view === 'string') : [];
      }),
  );

  const missing = queried.filter((name) => describable.has(name) && !described.has(name));
  if (missing.length === 0) {
    return;
  }

  const schema = missing
    .map((name) => {
      const doc = input.deps.getProvider?.(KNOWLEDGE_GRAPH_GROUP, name)?.describe?.(name);
      return doc ? formatViewSchemaMarkdown(name, doc) : `### ${name}\n(schema unavailable)`;
    })
    .join('\n\n');

  throw new ViewsNotDescribedError(missing, schema);
};

/**
 * QueryKnowledgeGraphTool — s-expr-driven CDF Data Model query.
 *
 * The LLM writes an s-expression that evaluates to a `Dataset`
 * (typically a `LazyDataset` over a CDF view provider source).
 * `execute` evaluates
 * **and materialises** it (one CDF round-trip), then returns a generic
 * `DatasetSummary` (column profile + row sample + data-quality notes)
 * so the LLM sees real data and any CDF failure surfaces through the
 * structured error envelope. The result is referenced by
 * `args.outputDataset` name in downstream tools
 * (`(-> (col <outputDataset>) …)`) instead of re-passing the
 * s-expression. The cross-turn namespace is a pure projection of the
 * active message branch: this tool-call part *is* the binding — a
 * downstream `(col <outputDataset> …)` re-derives the value by
 * re-evaluating this expression, scoped to the producers before it.
 * Nothing is staged, committed, or persisted separately; a
 * failed/streaming call simply isn't a producer. Pick a unique
 * `outputDataset` (if a name is reused on a branch the last successful
 * producer wins). The materialisation is cached on `session.fetch` so
 * the downstream re-derivation and `render`'s `useMaterializedExpr`
 * reuse it without a second round-trip.
 *
 * No per-call EvalContext assembly — `<ChatSessionProvider>` populates
 * `EvalDepsContext` once for the whole conversation, and every hook
 * below reads from it.
 *
 * The full s-expression grammar is documented once in the shared
 * `<ExprDslReference>` system-prompt block (see `AtlasInstructions`),
 * not re-embedded per tool.
 */
export function QueryKnowledgeGraphTool(props: ToolProps) {
  const scoped = useThreadScopedEval();
  const materialize = useMaterialize();
  const deps = useEvalDeps();
  const aui = useAui();
  const tools = use(ToolsContext);
  return (
    <Tool<QueryKnowledgeGraphInput, QueryKnowledgeGraphOutput>
      collapsible
      color="grape"
      description={
        <ToolGuidance
          whenToUse={[
            'Look up entities by name',
            'Discover relationships between entities',
            'Fetch entity metadata, KPI values (latest datapoints), and timeseries references',
          ]}
        >
          <p>
            <strong>Describe views before you query.</strong> Before referencing any knowledge-graph view in a{' '}
            {tools.QueryKnowledgeGraph} call, first call {tools.DescribeViews} for every view you will touch, then write
            the query against the exact column names and types it returns. A query against an un-described view is
            rejected.
          </p>
          <p>
            Query a CDF Data Model view via an s-expression returning a Dataset. The query is executed and the result
            materialised — you receive a column-profile summary and the result is bound under <code>outputDataset</code>{' '}
            for downstream <code>transformDataset</code>/<code>showDashboard</code> calls.
          </p>
          <example name="kg-query-to-dashboard">
            <p>User: "Show the 10 highest-capacity production wells with their latest output."</p>
            <ol>
              <li>
                <code>queryKnowledgeGraph</code> — expression{' '}
                <Expr>{`(-> (query "WellAsset" (filter #(= (:type %) "production")) (limit 10)) (sort [:capacity :desc]))`}</Expr>
                , outputDataset <code>top-wells</code> → binds <code>top-wells</code> (<code>capacity</code> is a static
                scalar — already display-ready; <code>outputRef</code> is a <code>ref&lt;timeseries&gt;</code> and is
                NOT)
              </li>
              <li>
                <code>transformDataset</code> — resolve the ts-ref to a scalar before display: expression{' '}
                <Expr>{`(-> top-wells (time-bucket [:outputRef] "1d" :window ["<start>" "<end>"] :agg [:last]) (unnest [:outputRef] :as [:day :out]) (filter #(is-not-null (:out %))) (sort [:day :desc]) (aggregate :group-by [:name :capacity] :pairs {:out_now (first :out) :as_of (first :day)}))`}</Expr>
                , outputDataset <code>top-resolved</code> → binds <code>top-resolved</code>
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <Expr>{`(dashboard :title "Top producing wells" (table (column :ref (col top-resolved name) :label "Well" :format text) (column :ref (col top-resolved capacity) :label "Capacity" :format number) (column :ref (col top-resolved out_now) :label "Latest output" :format number) (column :ref (col top-resolved as_of) :label "As of" :format datetime) :span [12 6]))`}</Expr>
              </li>
            </ol>
          </example>
          <p>
            <strong>Filtering by ids from another query:</strong> use the cross-binding{' '}
            <code>(in (col &lt;ThisView&gt; &lt;relationColumn&gt; externalId) (col &lt;binding&gt; externalId))</code>{' '}
            pattern documented in the DSL reference.{' '}
            <strong>Zero rows on a relation filter is a guess, not an answer</strong> — if you compared a relation
            column to a literal id string instead of chaining against a prior binding, re-query the Entity view first to
            obtain real instanceIds, bind them, then chain the relation filter against that binding before concluding
            the data does not exist.
          </p>
        </ToolGuidance>
      }
      execute={async (args: QueryKnowledgeGraphInput): Promise<QueryKnowledgeGraphOutput> => {
        // Describe-first guard: refuse before any CDF round trip if the query
        // references a describable view not yet described on this branch. The
        // thrown error embeds that view's schema so the agent retries with real
        // column names in one step.
        assertViewsDescribed({
          expression: args.expression,
          messages: aui.thread().getState().messages,
          deps,
          describeViewsToolName: tools.DescribeViews,
        });
        const { evaluate } = scoped();
        const value = await evaluate(args.expression);
        const ds = expectDataset(value, 'queryKnowledgeGraph result', NO_POS);
        const { mat, summary } = await materializeAndSummarize({ ds, materialize });
        return {
          success: true,
          state: mat.state,
          source: sourceLabelOf(ds),
          outputDataset: args.outputDataset,
          summary,
          flowSignal: {
            narrowTools: [
              tools.DescribeViews,
              tools.TransformDataset,
              tools.QueryKnowledgeGraph,
              tools.ShowDashboard,
              tools.AskUserQuestion,
            ],
            appendMessages: [
              {
                role: 'user',
                content: 'Proceed to perform any additional queries or data transformations *sequentially*.',
              },
            ],
          },
        };
      }}
      icon={IconDatabaseShare}
      identityKey={expressionArgsIdentity}
      message={{
        result: ({ output }) => (
          <DatasetPreview
            binding={output.outputDataset}
            source={output.source}
            state={output.state}
            summary={output.summary}
          />
        ),
        error: ({ args, envelope, toolName }) => (
          <ToolErrorMessage
            envelope={envelope}
            recovery={
              <p>
                Expression: <code>{args.expression}</code>. Fix the s-expression — verify the view name and{' '}
                <code>(filter …)</code> predicate against <code>{tools.DescribeViews}</code> — then retry. Do not ask
                the user.
              </p>
            }
            toolName={toolName}
          />
        ),
      }}
      parameters={QueryKnowledgeGraphInputSchema}
      {...props}
    />
  );
}
